import { gql } from '@apollo/client';

import { addressFragment, propertyInfoFragment } from './api';

export const GET_LOCATION = gql`
  query getLocation($address: AddressInput!, $coordinates: CoordinatesInput) {
    location(address: $address, coordinates: $coordinates) {
      id
      address {
        ...AddressFields
      }
      marketCode
      propertyInfo {
        ...PropertyInfoFields
      }
    }
  }
  ${addressFragment}
  ${propertyInfoFragment}
`;

export const GET_LOCATION_ID = gql`
  query getLocationId($address: AddressInput!) {
    location(address: $address) {
      id
    }
  }
`;

export const GET_ELIGIBLE_PACKAGE = gql`
  query eligiblePackage($locationIds: [String]!, $group: String!) {
    eligiblePackage(locationIds: $locationIds, group: $group) {
      code
    }
  }
`;

export const PACKAGE_QUOTES_MUTATION = gql`
  mutation PackageQuotesMutation(
    $date: Date!
    $time: Time!
    $group: String!
    $locations: [LocationInput!]!
    $duration: Float
    $workers: Int
    $isFeeQuotes: Boolean!
  ) {
    packageQuotesV2(
      date: $date
      time: $time
      group: $group
      locations: $locations
      duration: $duration
      workers: $workers
      isRoomBasedInventory: true
    ) {
      id
      package {
        id
        name
      }
      locations {
        id
        marketCode
      }
      pricingStrategy
      pricing {
        amount
      }
      serviceQuotes {
        service {
          id
        }
        workers
        duration
        available
        minimumWorkers
        minimumBillableDurationHours
        pricingStrategy
      }
      feeQuotes @include(if: $isFeeQuotes) {
        id
        feeName
        pricingStrategy
        pricing {
          amount
          currency
        }
      }
      longDistance
      available
      duration
      customizable
      date
    }
  }
`;

export const CUSTOMIZE_PACKAGE_QUOTE = gql`
  mutation CustomizePackageQuote($packageQuoteId: ID!, $customServices: [CustomServiceInput!]!) {
    customizePackageQuote(packageQuoteId: $packageQuoteId, customServices: $customServices) {
      id
      package {
        id
        name
      }
      locations {
        id
      }
      pricingStrategy
      pricing {
        amount
      }
      serviceQuotes {
        service {
          id
        }
        workers
        duration
        pricingStrategy
      }
      feeQuotes {
        id
        feeName
        pricing {
          amount
        }
      }
      longDistance
      available
      duration
      customizable
    }
  }
`;

export const GENERATE_ADD_ONS = gql`
  mutation GenerateAddOns($packageQuoteId: ID!) {
    serviceQuotes(packageQuoteID: $packageQuoteId) {
      id
      service {
        id
        name
        short_description
        long_description
      }
      pricing {
        amount
      }
      available
      guaranteed
      start
      startTimezone
      duration
      workers
      pricingStrategy
    }
  }
`;

export const CREATE_RESERVATION = gql`
  mutation CreateReservation($reservation: ReservationInput!) {
    reserve(reservation: $reservation) {
      id
    }
  }
`;

export const GET_QUOTE_SUMMARY = gql`
  query quoteSummary($input: QuoteSummaryInput!) {
    quoteSummary(input: $input) {
      totals {
        adjustments {
          source
          percentage
          amount {
            amount
          }
        }
        subtotal {
          amount
        }
        total {
          amount
        }
        deposit {
          amount
        }
      }
    }
  }
`;

export const GET_QUOTE_SUMMARY_V2 = gql`
  query quoteSummaryV2($input: QuoteSummaryInput!) {
    quoteSummaryV2(input: $input) {
      totals {
        adjustments {
          source
          percentage
          amount {
            amount
          }
        }
        subtotal {
          amount
        }
        total {
          amount
        }
        deposit {
          amount
        }
      }
    }
  }
`;

export const CREATE_FLEXIBLE_PACKAGE_QUOTES = gql`
  mutation flexiblePackageQuotesV2(
    $start: Date!
    $end: Date!
    $time: Time!
    $group: String!
    $packageID: ID!
    $locations: [LocationInput!]!
    $customServices: [CustomServiceInput!]
    $duration: Float
    $workers: Int
    $partnerCode: String
  ) {
    flexiblePackageQuotesV2(
      start: $start
      end: $end
      time: $time
      group: $group
      packageID: $packageID
      locations: $locations
      customServices: $customServices
      duration: $duration
      workers: $workers
      partnerCode: $partnerCode
      isRoomBasedInventory: true
    ) {
      id
      package {
        id
        name
      }
      locations {
        id
      }
      pricing {
        currency
        amount
      }
      pricingStrategy
      serviceQuotes {
        service {
          id
        }
        start
        startTimezone
        workers
        duration
        pricingStrategy
      }
      feeQuotes {
        id
        feeName
        pricing {
          amount
        }
      }
      available
      date
      times
      longDistance
      customizable
      duration
    }
  }
`;

export const CREATE_FLEXIBLE_SERVICE_QUOTES_V2 = gql`
  mutation CreateFlexibleServiceQuotesV2(
    $start: Date!
    $end: Date!
    $time: Time!
    $serviceID: ID!
    $packageQuoteID: ID!
    $locations: [LocationInput!]!
    $partnerCode: String
  ) {
    flexibleServiceQuotesV2(
      start: $start
      end: $end
      time: $time
      serviceID: $serviceID
      packageQuoteID: $packageQuoteID
      locations: $locations
      partnerCode: $partnerCode
    ) {
      id
      service {
        id
        name
      }
      pricing {
        currency
        amount
      }
      pricingStrategy
      available
      guaranteed
      start
      startTimezone
      end
      endTimezone
      duration
      workers
    }
  }
`;

export const GET_PACKAGE_QUOTE_BY_ID = gql`
  query getPackageQuote($id: ID!) {
    packageQuote(id: $id) {
      package {
        id
        name
        long_description
        short_description
        group_string
        services {
          id
          name
          description
          long_description
          short_description
          image_url
        }
      }
      serviceQuotes {
        id
        service {
          id
          name
          description
          long_description
          short_description
          image_url
        }
        locations {
          id
          address {
            ...AddressFields
          }
          propertyInfo {
            ...PropertyInfoFields
          }
          marketCode
        }
        pricingStrategy
        pricing {
          currency
          amount
        }
        duration
        workers
        available
        guaranteed
        fulfilled
        minimumBillableDurationHours
        minimumWorkers
        start
        startTimezone
        end
        endTimezone
      }
      feeQuotes {
        id
        pricingStrategy
        pricing {
          currency
          amount
        }
        feeName
        feeID
        feeDescription
      }
      locations {
        id
        address {
          ...AddressFields
        }
        propertyInfo {
          ...PropertyInfoFields
        }
        marketCode
      }
      recommended
      pricingStrategy
      pricing {
        currency
        amount
      }
      longDistance
      available
      duration
      customizable
      id
    }
  }
  ${addressFragment}
  ${propertyInfoFragment}
`;

export const GET_PACKAGE_QUOTE_BY_ID_IS_AVAILABLE = gql`
  query getPackageQuote($id: ID!) {
    packageQuote(id: $id) {
      available
    }
  }
`;

export const SAVE_QUOTE = gql`
  mutation SaveQuoteURL($quoteURL: String!, $LinkedOpportunitySFID: String!, $savedByUserEmail: String!) {
    saveQuoteURL(quoteURL: $quoteURL, LinkedOpportunitySFID: $LinkedOpportunitySFID, savedByUserEmail: $savedByUserEmail)
  }
`;
